import React, { useEffect, forwardRef } from 'react';
import { User, LogOut, KeyRound } from 'lucide-react';
import { useDashboardStore } from '../../store/dashboardStore';
import { signOutAuth } from '../../lib/supabaseAuth';

interface HeaderProps {
  userName?: string;
  /** 指定時のみ PIN変更・ログアウトを表示（デスクトップ用） */
  onLogout?: () => Promise<void>;
  /** スマホ時のみ safe-area 等を適用（デスクトップは元のスタイルのまま） */
  isMobile?: boolean;
}

export const Header = forwardRef<HTMLElement, HeaderProps>(function Header(
  { userName = '田中太郎', onLogout, isMobile = false },
  ref
) {
  const { currentUser, setShowPinChangeDialog, pinChangeMessage, setPinChangeMessage } = useDashboardStore();

  useEffect(() => {
    if (!pinChangeMessage) return;
    const timer = setTimeout(() => setPinChangeMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [pinChangeMessage, setPinChangeMessage]);

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem('sessionToken');
      if (onLogout) {
        await onLogout();
        return;
      }
      await signOutAuth();
      window.location.assign('/login');
    } catch (e) {
      window.location.assign('/login');
    }
  };

  const handleChangePin = () => {
    if (!currentUser?.id) {
      window.alert('PINを変更するには、一度ログアウトしてから再度ログインしてください。');
      return;
    }
    setShowPinChangeDialog(true);
  };

  const handleLogoClick = () => {
    useDashboardStore.setState((state) => ({
      showWorkflowStaffListPanel: false,
      showDatePanel: false,
      showDetailPanel: false,
      showSettingsPanel: false,
      showSpeechDictionaryPanel: false,
      showAllPatientListPanel: false,
      showStaffListPanel: false,
      showPatientFormPanel: false,
      showStaffFormPanel: false,
      showSettingsDatePanel: false,
      showSchedulePanel: false,
      showSchedulePatientListPanel: false,
      selectedPatientId: null,
      scheduleSelectedStaffId: null,
      scheduleSelectedTime: null,
      selectedDate: null,
      hasUserSelectedDate: false,
      editingPatient: null,
      editingStaff: null,
      datePanelRevision: state.datePanelRevision + 1,
      detailPanelRevision: state.detailPanelRevision + 1,
      settingsPanelRevision: state.settingsPanelRevision + 1,
      patientListRevision: state.patientListRevision + 1,
      staffListRevision: state.staffListRevision + 1,
      workflowStaffListRevision: state.workflowStaffListRevision + 1,
      settingsDatePanelRevision: state.settingsDatePanelRevision + 1,
      schedulePanelRevision: state.schedulePanelRevision + 1,
      schedulePatientListRevision: state.schedulePatientListRevision + 1,
      patientFormRevision: state.patientFormRevision + 1,
      staffFormRevision: state.staffFormRevision + 1,
      speechDictionaryRevision: state.speechDictionaryRevision + 1,
    }));
  };

  return (
    <header
      ref={ref}
      className={`fixed top-0 left-0 right-0 border-b z-[60] flex items-center justify-between px-6 ${
        isMobile ? 'min-h-[60px]' : 'h-[60px]'
      }`}
      style={{
        backgroundColor: '#00a48d',
        ...(isMobile && {
          paddingTop: 'var(--safe-area-top)',
          paddingBottom: '12px',
        }),
      }}
    >
      <button
        onClick={handleLogoClick}
        className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
      >
        <img
          src="/logo_icon_white.svg"
          alt=""
          className="w-8 h-8 object-contain"
          aria-hidden
        />
        <h1 className="flex items-baseline gap-2 text-white">
          <span className="text-xl font-semibold">Handra</span>
          <span className="text-[10px] font-normal">歯科医院申し送りシステム</span>
        </h1>
      </button>

      <div className="flex items-center space-x-4">
        {onLogout && pinChangeMessage && (
          <div
            className={`text-xs px-2 py-1 rounded ${
              pinChangeMessage.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-300'
                : 'bg-red-100 text-red-800 border border-red-300'
            }`}
          >
            {pinChangeMessage.text}
          </div>
        )}
        <div className="flex items-center space-x-2 text-sm text-white">
          <User size={16} />
          <span>{userName}</span>
        </div>
        {onLogout && (
          <>
            <button
              onClick={handleChangePin}
              className="flex items-center justify-center p-2 text-white transition-colors border border-white rounded hover:bg-white hover:text-green-600"
              aria-label="PINを変更"
              title="PINを変更"
            >
              <KeyRound size={20} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center justify-center p-2 text-white transition-colors border border-white rounded hover:bg-white hover:text-green-600"
              aria-label="ログアウト"
            >
              <LogOut size={20} />
            </button>
          </>
        )}
      </div>
    </header>
  );
});
